import React from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { scale } from '@components/frame';
import { convertTimeLetter, isAndroid, Popup } from '@helpers';
import TouchableElement from '@components/common/touchableElement';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import Calendar from './calendar';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useHandleNavigationContextHeader } from '@v2/pages/common/hooks/useHandleNavigationContextHeader';

const RenderElement = ({ name, time, C, loaded, dateText, color, colorDot, showDot, setSelectedChartDate, selectedChartDate }) => {
  const showCalendar = () => {
    Popup({
      Component: (p) => <Calendar {...{ ...p, C, setSelectedChartDate, selectedChartDate }} />,
      avoidingViewEnable: false,
    });
  };

  return (
    <TouchableElement onPress={showCalendar}>
      {({ boxStyle }) => (
        <View style={[boxStyle, { marginTop: scale(9), height: scale(81), width: '50%' }]}>
          <Text
            style={[
              {
                color,
              },
              font['Button 2'],
            ]}
          >
            {i18n.t(name)}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                opacity: loaded ? 1 : 0,
              }}
            >
              <Text
                style={[
                  {
                    color,
                  },
                  font['Large Title'],
                ]}
              >
                {Number(time.h)}
                <Text
                  style={[
                    font['Button 1'],
                    {
                      color,
                    },
                    isAndroid && { lineHeight: scale(36) },
                  ]}
                >
                  {i18n.t('h')}{' '}
                </Text>
              </Text>
              {Number(time.h) < 100 && (
                <Text
                  style={[
                    {
                      color,
                    },
                    font['Large Title'],
                  ]}
                >
                  {Number(time.m)}
                  <Text
                    style={[
                      {
                        color,
                      },
                      font['Button 1'],
                      isAndroid && { lineHeight: scale(36) },
                    ]}
                  >
                    {i18n.t('m')}
                  </Text>
                </Text>
              )}
            </View>
          </View>
          <View style={[{ flexDirection: 'row', alignItems: 'center', opacity: loaded ? 1 : 0 }]}>
            {showDot && (
              <View
                style={{
                  width: scale(10),
                  height: scale(10),
                  borderRadius: scale(10) / 2,
                  backgroundColor: colorDot || color,
                  marginRight: scale(3),
                }}
              />
            )}
            <Text
              style={[
                {
                  color,
                },
                font['Button 1'],
              ]}
            >
              {dateText}
            </Text>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

export default ({ C, triggerLine, showLine, dataPrev, data, setSelectedChartDate, selectedChartDate, isActive, chartTitle }) => {
  const { timePeriod: selectedTime } = useHandleParams();
  const loaded = data.start;
  const time = convertTimeLetter(isActive ? data.totalActive : data.totalProductive);
  const timePrev = convertTimeLetter(isActive ? dataPrev.totalActive : dataPrev.totalProductive);

  const renderAverage = (mString, mEndString) => {
    let m = moment(mString);
    let mEnd = moment(mEndString);
    if (!m || !mEnd) {
      m = moment();
      mEnd = moment();
    }

    switch (selectedTime) {
      case 'year':
        m = m.startOf('year');
        return `${m.format('YYYY')}`;
      case 'month':
        return `${i18n.t(m.format('MMM'))} ${m.format('YYYY')}`;
      case 'week':
        const iso = m.isoWeek();
        const isThisWeek = iso === moment().isoWeek();
        m = m.startOf('week');
        mEnd = mEnd.endOf('week');
        const currentYear = moment().format('YYYY');
        const startYear = m.format('YYYY');

        const period = `${m.format('DD')} ${i18n.t(m.format('MMM'))} - ${mEnd.format('DD')} ${i18n.t(mEnd.format('MMM'))} ${
          currentYear !== startYear ? startYear : ''
        }`;
        if (isThisWeek) {
          return `${i18n.t('This Week')}`;
        }
        const isLastWeek = iso === moment().subtract(1, 'week').isoWeek();
        if (isLastWeek) {
          return `${i18n.t('Last Week')}`;
        }
        return `${i18n.t('Week')} ${iso}`;
      case 'day':
        const isToday = m.isSame(moment().startOf('day'), 'd');
        if (isToday) {
          return `${i18n.t('Today')}`;
        }
        const isYesterday = m.isSame(moment().subtract(1, 'days').startOf('day'), 'd');
        if (isYesterday) {
          return `${i18n.t('Yesterday')}`;
        }
        return `${i18n.t(m.format('ddd'))} ${m.format('DD')} ${i18n.t(m.format('MMM'))} ${i18n.t(m.format('YYYY'))}`;
    }
  };

  const color = {
    [MachinesScreen.ACTIVE]: C('Status/Light/Active'),
    [MachinesScreen.PRODUCTIVE]: C('Status/Light/Productive'),
  };

  if (showLine) {
    return (
      <View style={{ width: '100%', flexDirection: 'row', height: scale(81), zIndex: 10000 }}>
        <RenderElement
          {...{
            name: chartTitle,
            time,
            C,
            loaded,
            dateText: renderAverage(data.start, data.end),
            color: color[chartTitle],
            showDot: true,
            setSelectedChartDate,
            selectedChartDate,
          }}
        />
        <RenderElement
          {...{
            name: chartTitle,
            time: timePrev,
            C,
            loaded,
            dateText: renderAverage(dataPrev.start, dataPrev.end),
            color: C('Label/Light/Tertiary'),
            colorDot: C('Gray/Light/3'),
            showDot: true,
            setSelectedChartDate,
            selectedChartDate,
          }}
        />
        <TouchableElement hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }} onPress={triggerLine}>
          {({ boxStyle }) => {
            return (
              <View
                style={[
                  boxStyle,
                  {
                    height: '100%',
                    width: scale(40),
                    position: 'absolute',
                    right: -scale(14),
                    alignItems: 'flex-end',
                    paddingTop: scale(12),
                    paddingRight: scale(14),
                  },
                ]}
              >
                <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
              </View>
            );
          }}
        </TouchableElement>
      </View>
    );
  }

  return (
    <View style={{ width: '100%', flexDirection: 'row', height: scale(81), zIndex: 10000 }}>
      <TouchableElement hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }} onPress={triggerLine}>
        {({ boxStyle }) => {
          return (
            <View
              style={[
                boxStyle,
                {
                  height: '100%',
                  width: scale(40),
                  position: 'absolute',
                  left: -scale(14),
                  paddingLeft: scale(14),
                  paddingTop: scale(12),
                  zIndex: 10000,
                },
              ]}
            >
              <Svg icon="chevronLeft" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
            </View>
          );
        }}
      </TouchableElement>
      <View style={{ paddingLeft: scale(15), width: '100%' }}>
        <RenderElement
          {...{
            name: chartTitle,
            time,
            C,
            loaded,
            dateText: renderAverage(data.start, data.end),
            color: color[chartTitle],
            showDot: false,
            setSelectedChartDate,
            selectedChartDate,
          }}
        />
      </View>
    </View>
  );
};
